import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Save, AlertTriangle, Plus, Edit, Trash2, Search, Upload } from 'lucide-react';
import { productAPI, parseFileData, stockSettingsAPI } from '../utils/api';
import ProductModal from '../components/ProductModal';
import '../styles/ClosingStock.css';

function ClosingStock() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [threshold, setThreshold] = useState(10);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const fileInputRef = useRef(null);

  const loadData = async (overrideThreshold) => {
    try {
      setLoading(true);
      const response = await productAPI.getAll();
      const data = response.data || [];
      setRows(data);
      setFilteredRows(data);
    } catch (error) {
      setMessage('Failed to load closing stock data');
    } finally {
      setLoading(false);
    }
  };

  const loadThreshold = async () => {
    try {
      const response = await stockSettingsAPI.getWarningThreshold();
      const value = Number(response.data?.value ?? 10);
      setThreshold(value);
      await loadData(value);
    } catch (_error) {
      await loadData(10);
    }
  };

  useEffect(() => {
    loadThreshold();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredRows(rows);
      return;
    }

    const q = searchQuery.toLowerCase();
    setFilteredRows(
      rows.filter((r) =>
        (r.productName || '').toLowerCase().includes(q) ||
        (r.category || '').toLowerCase().includes(q) ||
        (r.hsnCode || '').toLowerCase().includes(q)
      )
    );
  }, [searchQuery, rows]);

  const lowCount = useMemo(() => rows.filter((r) => Number(r.stockQuantity || 0) <= Number(threshold || 0)).length, [rows, threshold]);

  const handleSaveThreshold = async () => {
    try {
      setSaving(true);
      await stockSettingsAPI.setWarningThreshold(Number(threshold || 0));
      setMessage('Low stock warning setting saved');
      await loadData(Number(threshold || 0));
    } catch (error) {
      setMessage('Failed to save low stock warning setting');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 2500);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productAPI.delete(id);
      setMessage('Product deleted successfully');
      await loadData();
      setTimeout(() => setMessage(''), 2500);
    } catch (_error) {
      setMessage('Failed to delete product');
      setTimeout(() => setMessage(''), 2500);
    }
  };

  const handleSaveProduct = async (formData) => {
    try {
      if (editingProduct) {
        await productAPI.update(editingProduct.id, formData);
        setMessage('Product updated successfully');
      } else {
        await productAPI.create(formData);
        setMessage('Product created successfully');
      }
      setShowModal(false);
      await loadData();
      setTimeout(() => setMessage(''), 2500);
    } catch (_error) {
      setMessage('Failed to save product');
      setTimeout(() => setMessage(''), 2500);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      const products = await parseFileData(file);
      await productAPI.bulkCreate(products);
      setMessage(`Successfully imported ${products.length} products`);
      await loadData();
      setTimeout(() => setMessage(''), 2500);
    } catch (_error) {
      setMessage('Error importing products');
      setTimeout(() => setMessage(''), 2500);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="closing-stock-container">
      <div className="closing-stock-header">
        <h1>Closing Stock & Product Management</h1>
        <div className="threshold-controls">
          <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>
            <Upload size={16} />
            Import CSV/Excel
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <button className="btn btn-primary" onClick={handleAddProduct}>
            <Plus size={16} />
            Add Product
          </button>
          <label htmlFor="threshold">Low Quantity Warning</label>
          <input
            id="threshold"
            type="number"
            min="0"
            step="1"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
          <button className="btn btn-primary" onClick={handleSaveThreshold} disabled={saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {message && <div className="alert alert-success">{message}</div>}

      <div className="card">
        <div className="summary-row">
          <div className="summary-item">
            <span>Total Products</span>
            <strong>{rows.length}</strong>
          </div>
          <div className="summary-item warning">
            <span><AlertTriangle size={16} /> Low Quantity Items</span>
            <strong>{lowCount}</strong>
          </div>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : (
          <>
          <div className="search-bar">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by product name, HSN code, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>HSN</th>
                  <th>Purchase Price</th>
                  <th>Selling Price</th>
                  <th>Current Stock</th>
                  <th>Warning Threshold</th>
                  <th>Tax Rate</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const isLow = Number(row.stockQuantity || 0) <= Number(threshold || 0);
                  return (
                    <tr key={row.id} className={isLow ? 'low-row' : ''}>
                      <td>{row.productName}</td>
                      <td>{row.category || '-'}</td>
                      <td>{row.hsnCode || '-'}</td>
                      <td>₹{Number(row.purchasePrice ?? 0).toFixed(2)}</td>
                      <td>₹{Number(row.sellingPrice ?? row.mrp ?? row.rate ?? 0).toFixed(2)}</td>
                      <td>{Number(row.stockQuantity || 0).toFixed(2)}</td>
                      <td>{Number(threshold || 0).toFixed(2)}</td>
                      <td>{Number(row.taxRate || 0).toFixed(2)}%</td>
                      <td>
                        <span className={`status ${isLow ? 'low' : 'ok'}`}>
                          {isLow ? 'Low Stock' : 'Normal'}
                        </span>
                      </td>
                      <td className="action-buttons">
                        <button className="btn-icon edit" onClick={() => handleEditProduct(row)} title="Edit">
                          <Edit size={16} />
                        </button>
                        <button className="btn-icon delete" onClick={() => handleDeleteProduct(row.id)} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {showModal && (
        <ProductModal
          product={editingProduct}
          onSave={handleSaveProduct}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default ClosingStock;
